import React, { useState, useEffect } from 'react';
import {
  Activity,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  UserPlus,
  UserMinus,
  FileUp,
  FileX,
  Image as ImageIcon,
  TrendingUp,
  MessageSquare,
  Clock,
} from 'lucide-react';
import { getProjectActivityLogs } from '../../services/activityLogService';
import type { ProjectActivityLog as ActivityLog, ActivityActionType } from '../../types/team';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getUserColorFromId, getUserInitial } from '../../utils/userColor';
import { getThumbnailUrl } from '../../lib/imageStorage';

interface ProjectActivityLogProps {
  projectId: string;
  onSwitchToTasks?: () => void;
}

// アクションタイプに応じたアイコンとカラー
const getActivityIcon = (actionType: ActivityActionType) => {
  switch (actionType) {
    case 'created':
      return { icon: CheckCircle, color: 'text-green-600' };
    case 'updated':
      return { icon: Edit, color: 'text-blue-600' };
    case 'deleted':
      return { icon: Trash2, color: 'text-red-600' };
    case 'status_changed':
      return { icon: Activity, color: 'text-purple-600' };
    case 'assigned':
      return { icon: UserPlus, color: 'text-indigo-600' };
    case 'unassigned':
      return { icon: UserMinus, color: 'text-gray-600' };
    case 'completed':
      return { icon: CheckCircle, color: 'text-green-600' };
    case 'reopened':
      return { icon: XCircle, color: 'text-orange-600' };
    case 'file_uploaded':
      return { icon: FileUp, color: 'text-blue-600' };
    case 'file_deleted':
      return { icon: FileX, color: 'text-red-600' };
    case 'image_uploaded':
      return { icon: ImageIcon, color: 'text-pink-600' };
    case 'image_deleted':
      return { icon: ImageIcon, color: 'text-red-600' };
    case 'progress_updated':
      return { icon: TrendingUp, color: 'text-blue-600' };
    case 'comment_added':
      return { icon: MessageSquare, color: 'text-purple-600' };
    default:
      return { icon: Activity, color: 'text-gray-600' };
  }
};

// アクションタイプに応じた説明文
const getActivityDescription = (log: ActivityLog): string => {
  const details = log.details || {};

  switch (log.action_type) {
    case 'created':
      return `タスク「${details.task_title || ''}」を作成しました`;
    case 'updated':
      return `タスク「${details.task_title || ''}」を更新しました`;
    case 'deleted':
      return `タスク「${details.task_title || ''}」を削除しました`;
    case 'status_changed':
      return `タスク「${details.task_title || ''}」のステータスを「${details.old_status || ''}」から「${details.new_status || ''}」に変更しました`;
    case 'assigned':
      return `タスク「${details.task_title || ''}」を${log.assigned_user_nickname || 'メンバー'}に割り当てました`;
    case 'unassigned':
      return `タスク「${details.task_title || ''}」の担当者を解除しました`;
    case 'completed':
      return `タスク「${details.task_title || ''}」を完了しました`;
    case 'reopened':
      return `タスク「${details.task_title || ''}」を再開しました`;
    case 'file_uploaded':
      return `ファイル「${details.file_name || ''}」をアップロードしました`;
    case 'file_deleted':
      return `ファイル「${details.file_name || ''}」を削除しました`;
    case 'image_uploaded':
      return `画像「${details.file_name || ''}」をアップロードしました${details.task_title ? `（タスク: ${details.task_title}）` : ''}`;
    case 'image_deleted':
      return `画像「${details.file_name || ''}」を削除しました${details.task_title ? `（タスク: ${details.task_title}）` : ''}`;
    case 'progress_updated':
      return `進捗度を${details.old_progress || 0}%から${details.new_progress || 0}%に更新しました`;
    case 'comment_added':
      return `コメントを追加しました${details.progress_percentage ? ` (進捗: ${details.progress_percentage}%)` : ''}`;
    default:
      return 'アクションを実行しました';
  }
};

export const ProjectActivityLog: React.FC<ProjectActivityLogProps> = ({
  projectId,
  onSwitchToTasks,
}) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    loadActivityLogs();

    // リアルタイムイベントをリスン
    const handleActivityChange = () => {
      console.log('[ActivityLog] Realtime event received, reloading logs');
      loadActivityLogs();
    };

    window.addEventListener('activity-log-change', handleActivityChange);

    return () => {
      window.removeEventListener('activity-log-change', handleActivityChange);
    };
  }, [projectId]);

  const loadActivityLogs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getProjectActivityLogs(projectId, 50);
      setLogs(data);

      // 画像URLを取得
      const urls: Record<string, string> = {};
      for (const log of data) {
        if (log.action_type === 'image_uploaded' && log.details?.image_url) {
          try {
            const url = await getThumbnailUrl(log.details.image_url, 200, 200);
            if (url) {
              urls[log.id] = url;
            }
          } catch (err) {
            console.error('Error loading thumbnail:', err);
          }
        }
      }
      setImageUrls(urls);
    } catch (err) {
      console.error('Error loading activity logs:', err);
      setError('活動ログの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <XCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={loadActivityLogs}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          再読み込み
        </button>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
        <p className="text-gray-500 dark:text-gray-400 text-lg">
          まだ活動がありません
        </p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
          タスクを作成すると活動ログが表示されます
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          活動ログ
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {logs.length}件の活動
        </span>
      </div>

      {/* タイムライン */}
      <div className="relative">
        {/* 縦線 */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

        {/* ログアイテム */}
        <div className="space-y-2">
          {logs.map((log, index) => {
            const { icon: Icon, color } = getActivityIcon(log.action_type);
            const description = getActivityDescription(log);

            return (
              <div key={log.id} className="relative flex gap-3">
                {/* アイコン */}
                <div
                  className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 border-2 flex-shrink-0 ${
                    index === 0
                      ? 'border-blue-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>

                {/* コンテンツ */}
                <div className="flex-1 pb-2">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                    {/* ユーザー情報とアクション説明を1行に */}
                    <div className="flex items-start gap-2 mb-1">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${getUserColorFromId(log.user_id)} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white font-semibold text-[10px]">
                          {getUserInitial(log.user_nickname, null)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">
                            {log.user_nickname || '不明なユーザー'}
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {description}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {format(new Date(log.created_at), 'yyyy/MM/dd HH:mm:ss', {
                            locale: ja,
                          })}
                        </div>
                      </div>
                    </div>

                    {/* 追加情報（コメントのプレビューなど） */}
                    {log.action_type === 'comment_added' &&
                      log.details?.message_preview && (
                        <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-400 italic">
                          "{log.details.message_preview}"
                        </div>
                      )}

                    {/* ファイル情報表示 */}
                    {log.action_type === 'file_uploaded' && log.details?.file_url && (
                      <div className="mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                        <a
                          href={log.details.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <FileUp className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium truncate">{log.details.file_name}</span>
                          {log.details.file_size && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                              ({(log.details.file_size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          )}
                        </a>
                      </div>
                    )}

                    {/* 画像プレビュー表示 */}
                    {log.action_type === 'image_uploaded' && imageUrls[log.id] && (
                      <div className="mt-1">
                        <img
                          src={imageUrls[log.id]}
                          alt={log.details?.file_name || '画像'}
                          className="max-w-[200px] rounded border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity cursor-pointer"
                          loading="lazy"
                        />
                        {log.details?.file_name && (
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {log.details.file_name}
                          </p>
                        )}
                        <button
                          onClick={async () => {
                            if (log.details?.todo_id && onSwitchToTasks) {
                              onSwitchToTasks();
                              setTimeout(() => {
                                const taskElement = document.getElementById(`task-${log.details?.todo_id}`);
                                if (taskElement) {
                                  taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  taskElement.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2');
                                  setTimeout(() => {
                                    taskElement.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2');
                                  }, 5000);
                                }
                              }, 100);
                            }
                          }}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                        >
                          <CheckCircle className="w-3 h-3" />
                          タスクを表示
                        </button>
                      </div>
                    )}

                    {/* タスクへのリンク（todo_idがある場合） */}
                    {log.details?.todo_id && (log.action_type === 'created' || log.action_type === 'file_uploaded') && (
                      <div className="mt-1">
                        <a
                          href={`#task-${log.details.todo_id}`}
                          className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            // タスクタブに切り替え
                            if (onSwitchToTasks) {
                              onSwitchToTasks();
                            }
                            // 少し待ってからスクロール（タブ切り替えのレンダリングを待つ）
                            setTimeout(() => {
                              const taskElement = document.getElementById(`task-${log.details?.todo_id}`);
                              if (taskElement) {
                                taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                // ハイライト効果（5秒間）
                                taskElement.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2');
                                setTimeout(() => {
                                  taskElement.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2');
                                }, 5000);
                              }
                            }, 100);
                          }}
                        >
                          <CheckCircle className="w-3 h-3" />
                          タスクを表示
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 最後まで読み込んだメッセージ */}
      {logs.length >= 50 && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            最新50件の活動を表示しています
          </p>
        </div>
      )}
    </div>
  );
};
